import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  StatusCode,
  type TagGroupDetail,
  getApiV2TagGroupById,
} from '@hungryroot/rest';

type UseTagGroupOptions = Omit<
  UseQueryOptions<TagGroupDetail>,
  'queryFn' | 'queryKey'
> & {
  query?:
    | URLSearchParams
    | Record<string, string | number | boolean | null | undefined>;
};

export function useTagGroup(
  tagGroupId: number,
  options: UseTagGroupOptions = {}
) {
  const { query, ...rq } = options;

  const params =
    query instanceof URLSearchParams
      ? new URLSearchParams(query) // clone
      : new URLSearchParams(
          Object.entries(query ?? {}).reduce<Record<string, string>>(
            (a, [k, v]) => {
              if (v == null) return a;
              a[k] = String(v);
              return a;
            },
            {}
          )
        );

  const qs = params.toString(); // canonical string for both key + request

  return useQuery({
    ...rq,
    queryKey: ['tag group', tagGroupId, qs],
    queryFn: async () => {
      const resp = await getApiV2TagGroupById(
        tagGroupId,
        new URLSearchParams(qs)
      );
      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }
      return resp.data;
    },
  });
}
